import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, TrendingUp, Calendar, DollarSign, Plus, Pencil, Upload } from 'lucide-react';
import { trackTradeDeleted } from '../../services/analytics';
import ScreenshotThumbnail from '../../components/ui/ScreenshotThumbnail';
import { getTradeScreenshots } from '../../services/screenshotService';
import { fetchTradesFromDB, deleteTradeFromDB, sendTradeToDB } from '../journaling/JournalingService';
import EditTradeModal from './EditTradeModal';
import AddTradeModal from './AddTradeModal';
import ImportTradesModal from './ImportTradesModal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { isBuyDirection } from '../../utils/tradeCalculations';

const Card = ({ children, className = '' }) => (
  <div className={`bg-card text-card-foreground rounded-lg shadow-sm border border-border p-6 ${className}`}>{children}</div>
);

const TableTraderMetrics = ({ trade }) => {
  // Try to extract metrics from summary_data, ai_summary, or analysis
  let metrics = null;
  if (trade.summary_data && typeof trade.summary_data === 'object') {
    metrics = trade.summary_data;
  } else if (trade.ai_summary) {
    try {
      if (typeof trade.ai_summary === 'string') {
        const parsed = JSON.parse(trade.ai_summary);
        if (parsed && (parsed.emotionalstate || parsed.risk_management || parsed.strategyadherence)) {
          metrics = parsed;
        }
      } else if (typeof trade.ai_summary === 'object') {
        metrics = trade.ai_summary;
      }
    } catch (e) {}
  } else if (trade.analysis) {
    metrics = trade.analysis;
  }

  // Only render if at least one metric is present
  const hasMetrics = metrics && (
    metrics.emotionalstate !== undefined ||
    metrics.risk_management !== undefined ||
    metrics.strategyadherence !== undefined
  );
  if (!hasMetrics) return null;

  const getMetricColor = (metric, value) => {
    if (value === null || value === undefined) return { bg: 'bg-white/[0.08]', fill: 0 };
    const fillPercentage = Math.min(value, 100);
    let bgColor;
    switch(metric) {
      case 'emotionalstate': bgColor = 'bg-[#1A6FE8]'; break;
      case 'risk_management': bgColor = 'bg-[#00D4C8]'; break;
      case 'strategyadherence': bgColor = 'bg-[#22C55E]'; break;
      default: bgColor = 'bg-white/[0.08]';
    }
    return { bg: bgColor, fill: fillPercentage };
  };

  return (
    <div className="min-w-[80px] w-24 flex flex-col gap-1 py-1">
      <div>
        <span className="text-xs text-muted-foreground">Emotion</span>
        <div className="h-1.5 w-full bg-white/[0.08] rounded overflow-hidden">
          <div className={`h-full rounded ${getMetricColor('emotionalstate', metrics.emotionalstate).bg}`} style={{ width: `${getMetricColor('emotionalstate', metrics.emotionalstate).fill}%` }}></div>
        </div>
      </div>
      <div>
        <span className="text-xs text-muted-foreground">Risk</span>
        <div className="h-1.5 w-full bg-white/[0.08] rounded overflow-hidden">
          <div className={`h-full rounded ${getMetricColor('risk_management', metrics.risk_management).bg}`} style={{ width: `${getMetricColor('risk_management', metrics.risk_management).fill}%` }}></div>
        </div>
      </div>
      <div>
        <span className="text-xs text-muted-foreground">Strategy</span>
        <div className="h-1.5 w-full bg-white/[0.08] rounded overflow-hidden">
          <div className={`h-full rounded ${getMetricColor('strategyadherence', metrics.strategyadherence).bg}`} style={{ width: `${getMetricColor('strategyadherence', metrics.strategyadherence).fill}%` }}></div>
        </div>
      </div>
    </div>
  );
};

const TradesView = () => {
  const [trades, setTrades] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  const [assetFilter, setAssetFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [filter, setFilter] = useState({
    search: '',
    direction: 'ALL',
    dateRange: 'ALL'
  });
  const formatProfit = (profit) => {
    //console.log('Formatting profit:', profit, 'Type:', typeof profit);
    try {
      if (profit === null || profit === undefined) return '0.00';
      const numProfit = Number(profit);
      if (isNaN(numProfit)) return '0.00';
      return numProfit.toFixed(2);
    } catch (error) {
      console.error('Error formatting profit:', error);
      return '0.00';
    }
  };

  const isPositiveProfit = (profit) => {
    try {
      if (profit === null || profit === undefined) return false;
      const numProfit = Number(profit);
      return !isNaN(numProfit) && numProfit > 0;
    } catch (error) {
      console.error('Error checking positive profit:', error);
      return false;
    }
  };

  useEffect(() => {
    loadTrades();
  }, []);

  const loadTrades = async () => {
    try {
      const tradesData = await fetchTradesFromDB();
      console.log('Loaded trades:', tradesData);
      // Ensure all trades have valid profit values
      const processedTrades = tradesData.map(trade => ({
        ...trade,
        profit: trade.profit ? Number(trade.profit) : 0
      }));
      console.log('Processed trades:', processedTrades);
      setTrades(processedTrades);
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  };

  const handleEdit = (trade) => {
    setSelectedTrade(trade);
    setIsEditModalOpen(true);
  };

  const handleDelete = (trade) => {
    setTradeToDelete(trade);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteTradeFromDB(tradeToDelete.id);
      trackTradeDeleted();
      await loadTrades();
      setIsDeleteModalOpen(false);
      setTradeToDelete(null);
    } catch (error) {
      console.error('Error deleting trade:', error);
    }
  };

  const handleEditComplete = async () => {
    await loadTrades();
    setIsEditModalOpen(false);
    setSelectedTrade(null);
  };

  const handleAddTrade = async (trade) => {
    try {
      const savedTrade = await sendTradeToDB(trade);
      await loadTrades();
      return savedTrade; // Modal uses the ID to upload screenshots before closing
    } catch (error) {
      console.error('Error adding trade:', error);
      alert(`Failed to add trade: ${error.message}`);
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'done') return <span className="px-2 py-1 rounded-full bg-[rgba(34,197,94,0.12)] text-[#4ADE80] border border-[rgba(34,197,94,0.20)] text-xs font-semibold">Done</span>;
    if (status === 'in_progress') return <span className="px-2 py-1 rounded-full bg-[rgba(26,111,232,0.12)] text-[#1A6FE8] border border-[rgba(26,111,232,0.20)] text-xs font-semibold">In Progress</span>;
    return <span className="px-2 py-1 rounded-full bg-[rgba(110,125,150,0.10)] text-[#94A3B8] border border-[rgba(110,125,150,0.20)] text-xs font-semibold">Need Journal</span>;
  };

  const getScoreColor = (value) => {
    if (value >= 80) return 'bg-[rgba(34,197,94,0.12)] text-[#4ADE80]';
    if (value >= 50) return 'bg-[rgba(234,179,8,0.12)] text-yellow-400';
    return 'bg-[rgba(239,68,68,0.12)] text-[#F87171]';
  };

  const ScoreBadge = ({ label, value }) => (
    <span className={`px-2 py-1 rounded text-xs font-bold ${getScoreColor(value)}`}>{label}: {value}</span>
  );

  const getMetricColor = (metric, value) => {
    if (value === null || value === undefined) return { bg: 'bg-white/[0.08]', fill: 0 };

    const fillPercentage = Math.min(value, 100); // Cap at 100%

    let bgColor;
    switch(metric) {
      case 'emotion':
        bgColor = 'bg-[#1A6FE8]';
        break;
      case 'risk':
        bgColor = 'bg-[#00D4C8]';
        break;
      case 'strategy':
        bgColor = 'bg-[#22C55E]';
        break;
      default:
        bgColor = 'bg-white/[0.08]';
    }

    return { bg: bgColor, fill: fillPercentage };
  };

  const TraderMetrics = ({ summary_data }) => (
    <div className="w-full flex flex-row items-center mt-4 border-t pt-3">
      <div className="flex flex-col justify-between mr-2 text-xs font-bold text-muted-foreground h-12 min-w-[60px]">
        <span>Emotion</span>
        <span>Risk</span>
        <span>Strategy</span>
      </div>
      <div className="flex flex-col w-full gap-1">
        {/* Emotion Bar */}
        <div className="h-2 w-full bg-white/[0.08] rounded overflow-hidden">
          <div
            className={`h-full rounded ${getMetricColor('emotion', summary_data?.emotionalstate).bg}`}
            style={{ width: `${getMetricColor('emotion', summary_data?.emotionalstate).fill}%` }}
          ></div>
        </div>

        {/* Risk Bar */}
        <div className="h-2 w-full bg-white/[0.08] rounded overflow-hidden">
          <div
            className={`h-full rounded ${getMetricColor('risk', summary_data?.risk_management).bg}`}
            style={{ width: `${getMetricColor('risk', summary_data?.risk_management).fill}%` }}
          ></div>
        </div>

        {/* Strategy Bar */}
        <div className="h-2 w-full bg-white/[0.08] rounded overflow-hidden">
          <div
            className={`h-full rounded ${getMetricColor('strategy', summary_data?.strategyadherence).bg}`}
            style={{ width: `${getMetricColor('strategy', summary_data?.strategyadherence).fill}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
  

  const renderProfit = (trade) => {
    const profit = trade.profit;
    const formattedProfit = formatProfit(profit);
    const isPositive = isPositiveProfit(profit);
    
    return (
      <div className="text-right">
        <p className="text-sm text-muted-foreground">Profit/Loss</p>
        <p className={`font-semibold font-mono tabular-nums ${isPositive ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
          {isPositive ? '+' : ''}{formattedProfit}
        </p>
      </div>
    );
  };

  const filteredTrades = trades.filter(trade => {
    const matchesSearch = trade.symbol.toLowerCase().includes(filter.search.toLowerCase()) ||
                         trade.notes?.toLowerCase().includes(filter.search.toLowerCase());
    const matchesTime = timeFilter === 'all' || (timeFilter === 'today' && new Date(trade.date).toDateString() === new Date().toDateString()) ||
                        (timeFilter === 'week' && new Date(trade.date).getTime() >= new Date().getTime() - 7 * 24 * 60 * 60 * 1000) ||
                        (timeFilter === 'month' && new Date(trade.date).getTime() >= new Date().getTime() - 30 * 24 * 60 * 60 * 1000);
    const matchesAsset = assetFilter === 'all' || trade.asset_type === assetFilter;
    const matchesDirection = filter.direction === 'ALL' || trade.direction === filter.direction ||
                            (filter.direction === 'BUY/LONG' && isBuyDirection(trade.direction)) ||
                            (filter.direction === 'SELL/SHORT' && !isBuyDirection(trade.direction));
    return matchesSearch && matchesTime && matchesAsset && matchesDirection;
  });

  const sortedTrades = filteredTrades.sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.date) - new Date(a.date);
    } else if (sortBy === 'profit') {
      return b.profit - a.profit;
    } else if (sortBy === 'pips') {
      return b.pips - a.pips;
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">Trades</h1>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Add Button Row */}
        <Card className="mb-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
              <div className="space-y-2">
                <label className="text-sm font-medium">Search</label>
                <Input
                  placeholder="Search trades..."
                  value={filter.search}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  className="bg-muted border-border"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Direction</label>
                <Select
                  value={filter.direction}
                  onValueChange={(value) => setFilter({ ...filter, direction: value })}
                >
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All</SelectItem>
                    <SelectItem value="BUY/LONG" className="text-green-500">BUY / LONG</SelectItem>
                    <SelectItem value="SELL/SHORT" className="text-red-500">SELL / SHORT</SelectItem>
                    <SelectItem value="BUY" className="text-green-500">BUY</SelectItem>
                    <SelectItem value="SELL" className="text-red-500">SELL</SelectItem>
                    <SelectItem value="LONG" className="text-green-500">LONG</SelectItem>
                    <SelectItem value="SHORT" className="text-red-500">SHORT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Date Range</label>
                <Select
                  value={filter.dateRange}
                  onValueChange={(value) => setFilter({ ...filter, dateRange: value })}
                >
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Time</SelectItem>
                    <SelectItem value="TODAY">Today</SelectItem>
                    <SelectItem value="WEEK">This Week</SelectItem>
                    <SelectItem value="MONTH">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <Button variant="outline" onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                <span>Import</span>
              </Button>
              <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                <span>New Trade</span>
              </Button>
            </div>
          </div>
        </Card>

        {/* Trades List */}
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-[#151E3A] border-b border-white/[0.06]">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Pair</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Direction</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Entry</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Exit</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Pips</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Profit/Loss</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Metrics</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-widest">Charts</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTrades.map((trade, idx) => (
                  <tr key={idx} className="border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors duration-100">
                    <td className="px-4 py-3">{trade.trade_date ? new Date(trade.trade_date).toLocaleDateString() : ''}</td>
                    <td className="px-4 py-3 font-medium">{trade.symbol}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isBuyDirection(trade.direction)
                          ? 'bg-[rgba(34,197,94,0.12)] text-[#4ADE80] border border-[rgba(34,197,94,0.20)]'
                          : 'bg-[rgba(239,68,68,0.12)] text-[#F87171] border border-[rgba(239,68,68,0.20)]'
                      }`}>
                        {trade.direction}
                      </span>
                    </td>
                    <td className="px-4 py-3">{trade.entry_price}</td>
                    <td className="px-4 py-3">{trade.exit_price}</td>
                    <td className="px-4 py-3">{trade.pips}</td>
                    <td className={`px-4 py-3 font-medium font-mono tabular-nums ${
                      trade.profit > 0
                        ? 'text-[#4ADE80]'
                        : 'text-[#F87171]'
                    }`}>
                      {trade.profit > 0 ? '+' : ''}{trade.profit}
                      {(parseFloat(trade.commission) || parseFloat(trade.swap)) ? (
                        <div className="text-xs text-muted-foreground font-normal mt-0.5">
                          Fees: {((parseFloat(trade.commission) || 0) + (parseFloat(trade.swap) || 0)).toFixed(2)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <TableTraderMetrics trade={trade} />
                    </td>
                    <td className="px-4 py-3">
                      {trade.screenshot_count > 0 && trade.first_screenshot_url ? (
                        <ScreenshotThumbnail
                          screenshots={[{
                            id: `${trade.id}-thumb`,
                            public_url: trade.first_screenshot_url,
                            file_name: `${trade.symbol} chart`,
                            screenshot_analysis: null,
                          }]}
                          maxVisible={1}
                          fetchAllScreenshots={() => getTradeScreenshots(trade.id)}
                        />
                      ) : null}
                      {trade.screenshot_count > 1 && (
                        <span className="text-xs text-muted-foreground ml-1">+{trade.screenshot_count - 1}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="inline-flex items-center p-1 text-blue-600 hover:text-blue-800"
                        onClick={() => handleEdit(trade)}
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        className="inline-flex items-center p-1 ml-2 text-[#F87171] hover:text-red-400"
                        onClick={() => handleDelete(trade)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedTrade && (
        <EditTradeModal
          trade={selectedTrade}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedTrade(null);
          }}
          onUpdate={handleEditComplete}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && tradeToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1A2544] border border-white/[0.08] rounded-xl p-6 max-w-sm w-full shadow-modal">
            <h3 className="text-lg font-semibold text-foreground mb-4">Delete Trade</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this trade? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setTradeToDelete(null);
                }}
                className="px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-white/[0.05] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 hover:border-red-500/50 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <AddTradeModal
          onClose={() => { setIsAddModalOpen(false); loadTrades(); }}
          onSave={handleAddTrade}
        />
      )}

      <ImportTradesModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={() => { setIsImportModalOpen(false); loadTrades(); }}
      />
    </div>
  );
};

export default TradesView; 